import queryString from 'query-string';

import { isEmpty, listenToWindowMessage, postWindowMessage } from '@groww-tech-pvt/ella';

import { WEBVIEW_PLATFORM_NAME } from './webviewConstants';
import {
  getWebviewAppHeaders,
  getWebViewAppVersion,
  getWebviewMetaData,
  getWebViewPlatform,
  getWebViewStatus,
  getWebViewStatusFromUserAgent,
  compareAppVersions,
  isAppVersionLessThan,
  isAppVersionGreaterThan,
  isAppVersionEqualTo
} from './webviewHelpers';
import { msgType, WebviewType } from './webviewTypes';

export default function(): WebviewType {

  const platformType = getWebViewPlatform();
  const isActive = getWebViewStatus();

  let webviewAppVersion:string = '';
  let webviewAppHeaders:object = {};
  let webviewMetaData:object = {};
  let webviewParams:object = {};


  /**
   * @returns This method will return the webview app version for both iOS and Android
   */
  const getAppVersion = () => {
    if (webviewAppVersion) {
      return webviewAppVersion;

    } else {
      webviewAppVersion = getWebViewAppVersion();

      return webviewAppVersion;
    }
  };


  /**
   * @returns This method will return the webview headers which will in turn be sent to the apis.
   * The method determines if we are in android or iOS and then prepares the headers accordingly. It also
   * sends the app version.
   */
  const getAppHeaders = () => {

    if (!isEmpty(webviewAppHeaders)) {
      return webviewAppHeaders;

    } else {
      webviewAppHeaders = getWebviewAppHeaders();

      return webviewAppHeaders;
    }
  };

  /**
   * @returns This method will return the webview meta data namely isWebview, webviewAppVersion
   * and webviewPlatform
   */
  const getMetaData = () => {
    if (!isEmpty(webviewMetaData)) {
      return webviewMetaData;

    } else {
      webviewMetaData = getWebviewMetaData();

      return webviewMetaData;
    }
  };

  /**
   * @returns This method will return all the params attached to the url from app through
   * queryString
   */
  const getParams = () => {
    if (!isEmpty(webviewParams)) {
      return webviewParams;

    } else {
      webviewParams = queryString.parse(window?.location?.search);

      return webviewParams;
    }
  };

  /**
   * This method takes an object and sends it to app through the postWindowMessage method
   *
   * @param postObj Information about the data to be sent to app.
   * @param postObj.action Some action name so that app can identify the event.
   * @param postObj.params Actual data to be sent to app.
   *
   * @remarks
   * function will work in webview only
   *
   * @example
   * ```
   * const postObj = {
   *   action: 'FIXED_DEPOSIT',
   *   params: {
   *     type: 'navigation',
   *     screen: 'FDBANKPAGE'
   *     }
   * };
   *
   * WV.sendMessage(postObj);
   * ```
   */
  function sendMessage(postObj: msgType) {
    if (isActive) {
      postWindowMessage(postObj);
    }
  }

  /**
   * This method will be used to receive a message from app side.
   *
   * @param {Function} eventCallback
   * @param {string} eventIdentifier
   *
   */
  function receiveMessage(eventCallback: Function, eventIdentifier: string = 'CUSTOM_MESSAGE') {
    if (isActive) {
      listenToWindowMessage(eventCallback, eventIdentifier);
    }
  }

  const isIOS = platformType === WEBVIEW_PLATFORM_NAME.IOS && isActive;
  const isAndroid = platformType === WEBVIEW_PLATFORM_NAME.ANDROID && isActive;
  const isActiveFromUserAgent = getWebViewStatusFromUserAgent();

  return {
    platformType,
    isActive,
    getAppVersion,
    getAppHeaders,
    getMetaData,
    getParams,
    sendMessage,
    receiveMessage,
    isIOS,
    isAndroid,
    ENUM_PLATFORM_TYPE: WEBVIEW_PLATFORM_NAME,
    isActiveFromUserAgent,
    compareAppVersions,
    isAppVersionLessThan,
    isAppVersionGreaterThan,
    isAppVersionEqualTo
  };
}

export {
  getWebviewAppHeaders,
  getWebViewAppVersion,
  getWebviewMetaData,
  getWebViewPlatform,
  getWebViewStatus,
  setWebViewAppVersion,
  getWebViewStatusFromUserAgent,
  compareAppVersions,
  isAppVersionLessThan,
  isAppVersionGreaterThan,
  isAppVersionEqualTo
} from './webviewHelpers';
